import type { CDMOApplication } from '../supabase';
import { mapToApplication, type CdmoImportContext } from './mapToApplication';
import {
  describeDuplicate,
  isImportableState,
  splitStoredFeiNumbers,
  type ExistingCdmoDomainMatch,
  type ExistingCdmoFeiMatch,
  type ValidatedCdmoImportRow,
} from './validateRow';

export type CdmoImportOutcome = 'created' | 'skipped_duplicate' | 'skipped_error' | 'skipped_by_admin' | 'failed';

/** One `cdmo_import_rows` record. */
export interface CdmoImportAuditRow {
  batch_id: string;
  file_name: string;
  row_number: number;
  company_name: string | null;
  normalized_domain: string | null;
  outcome: CdmoImportOutcome;
  cdmo_id: string | null;
  errors: string[];
  warnings: string[];
  review_data: Record<string, string>;
  raw_row: Record<string, string>;
  imported_by: string;
}

/**
 * What the import needs from the database. Injected so this module never touches the Supabase
 * client directly — and so it has no route to an email function: creating a profile is the only
 * write it can make besides its own audit rows.
 */
export interface CdmoImportDeps {
  findExistingByDomains(domains: string[]): Promise<ExistingCdmoDomainMatch[]>;
  /** Profiles already holding any of these FEI numbers. */
  findExistingByFeiNumbers(feiNumbers: string[]): Promise<ExistingCdmoFeiMatch[]>;
  createApplication(application: CDMOApplication): Promise<{ id?: string } | null>;
  insertAuditRows(rows: CdmoImportAuditRow[]): Promise<void>;
}

export interface CdmoImportRowResult {
  rowNumber: number;
  companyName: string;
  outcome: CdmoImportOutcome;
  /** The new profile, when created. */
  cdmoId?: string;
  /** The profile a duplicate collides with, when known. */
  existingId?: string;
  reason?: string;
}

export interface CdmoImportResult {
  batchId: string;
  results: CdmoImportRowResult[];
  counts: Record<CdmoImportOutcome, number>;
  /** Audit writes that failed. The profiles themselves are unaffected. */
  auditFailures: string[];
}

export interface ImportRowsParams {
  rows: readonly ValidatedCdmoImportRow[];
  /** Row numbers the admin left ticked. */
  selected: ReadonlySet<number>;
  context: CdmoImportContext;
  batchId: string;
  importedBy: string;
  deps: CdmoImportDeps;
  onProgress?: (done: number, total: number) => void;
  newFacilityId?: () => string;
}

/** The message of an `Error`, a Supabase error object or anything else thrown, for showing to the admin. */
export const errorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
};

/** A create failure in words an admin can act on. */
export function describeCreateError(error: unknown): string {
  const message = errorMessage(error) || 'Unknown error';
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : '';
  if (message.includes('unique_cdmo_fei_number') || (code === '23505' && /fei/i.test(message))) {
    return 'FEI number: already on another CDMO profile';
  }
  return message;
}

const countOutcomes = (results: readonly CdmoImportRowResult[]) => {
  const counts: Record<CdmoImportOutcome, number> = {
    created: 0,
    skipped_duplicate: 0,
    skipped_error: 0,
    skipped_by_admin: 0,
    failed: 0,
  };
  for (const result of results) counts[result.outcome] += 1;
  return counts;
};

/**
 * Creates one unclaimed, admin-created profile per ticked, valid row — one at a time, so one failure
 * never stops the rest — and records every uploaded row's outcome in `cdmo_import_rows`.
 *
 * Each row's domain and FEI numbers are checked again immediately before its insert, because another
 * admin may have created the same CDMO since the preview. Rows not being imported are audited up front in a single
 * write; each imported row is audited as soon as its outcome is known, so a closed tab still leaves
 * an accurate record of what was created.
 *
 * Sends no email and enrols nothing in a campaign.
 */
export async function importRows({
  rows,
  selected,
  context,
  batchId,
  importedBy,
  deps,
  onProgress,
  newFacilityId,
}: ImportRowsParams): Promise<CdmoImportResult> {
  const results: CdmoImportRowResult[] = [];
  const auditFailures: string[] = [];

  const auditRow = (
    row: ValidatedCdmoImportRow,
    outcome: CdmoImportOutcome,
    extra: { cdmoId?: string; errors?: string[] } = {}
  ): CdmoImportAuditRow => ({
    batch_id: batchId,
    file_name: context.fileName,
    row_number: row.rowNumber,
    company_name: row.companyName || null,
    normalized_domain: row.normalizedDomain,
    outcome,
    cdmo_id: extra.cdmoId ?? null,
    errors: extra.errors ?? row.errors,
    warnings: row.warnings,
    review_data: row.reviewData,
    raw_row: row.raw,
    imported_by: importedBy,
  });

  const writeAudit = async (auditRows: CdmoImportAuditRow[]) => {
    if (auditRows.length === 0) return;
    try {
      await deps.insertAuditRows(auditRows);
    } catch (error) {
      const rowsLabel = auditRows.map((r) => r.row_number).join(', ');
      auditFailures.push(`Row${auditRows.length > 1 ? 's' : ''} ${rowsLabel}: ${errorMessage(error)}`);
    }
  };

  const toImport: ValidatedCdmoImportRow[] = [];
  const skippedAudit: CdmoImportAuditRow[] = [];

  for (const row of rows) {
    if (row.state === 'error') {
      results.push({
        rowNumber: row.rowNumber,
        companyName: row.companyName,
        outcome: 'skipped_error',
        reason: `${row.errors.length} error${row.errors.length === 1 ? '' : 's'}: ${row.errors[0]}`,
      });
      skippedAudit.push(auditRow(row, 'skipped_error'));
    } else if (row.state === 'duplicate') {
      const reason = describeDuplicate(row) ?? 'Duplicate';
      results.push({
        rowNumber: row.rowNumber,
        companyName: row.companyName,
        outcome: 'skipped_duplicate',
        existingId: row.duplicate?.existingId,
        reason,
      });
      skippedAudit.push(auditRow(row, 'skipped_duplicate', { errors: [reason, ...row.errors] }));
    } else if (isImportableState(row.state) && !selected.has(row.rowNumber)) {
      results.push({
        rowNumber: row.rowNumber,
        companyName: row.companyName,
        outcome: 'skipped_by_admin',
        reason: 'Unticked before import',
      });
      skippedAudit.push(auditRow(row, 'skipped_by_admin'));
    } else {
      toImport.push(row);
    }
  }

  await writeAudit(skippedAudit);

  const total = toImport.length;
  onProgress?.(0, total);

  for (const [index, row] of toImport.entries()) {
    let result: CdmoImportRowResult;
    let audit: CdmoImportAuditRow;

    let existing: ExistingCdmoDomainMatch | undefined;
    let feiConflict: string | null = null;
    let recheckError: string | null = null;
    try {
      if (row.normalizedDomain) {
        [existing] = await deps.findExistingByDomains([row.normalizedDomain]);
      }
      if (!existing && row.profile.fei_numbers.length > 0) {
        const holders = await deps.findExistingByFeiNumbers(row.profile.fei_numbers);
        for (const holder of holders) {
          const fei = splitStoredFeiNumbers(holder.fei_number).find((f) => row.profile.fei_numbers.includes(f));
          if (fei) {
            feiConflict = `FEI number: "${fei}" was registered to "${holder.company_name.trim()}" after the preview — an FEI number can belong to only one profile`;
            break;
          }
        }
      }
    } catch (error) {
      recheckError = `Could not check for an existing profile before creating it: ${errorMessage(error)}`;
    }

    if (recheckError || feiConflict) {
      const reason = (recheckError ?? feiConflict) as string;
      result = { rowNumber: row.rowNumber, companyName: row.companyName, outcome: 'failed', reason };
      audit = auditRow(row, 'failed', { errors: [reason] });
    } else if (existing) {
      const reason = `Duplicate: website domain "${row.normalizedDomain}" was added as "${existing.company_name}" after the preview`;
      result = {
        rowNumber: row.rowNumber,
        companyName: row.companyName,
        outcome: 'skipped_duplicate',
        existingId: existing.id,
        reason,
      };
      audit = auditRow(row, 'skipped_duplicate', { errors: [reason] });
    } else {
      try {
        const application = mapToApplication(row.profile, row.review, context, newFacilityId);
        const created = await deps.createApplication(application);
        result = { rowNumber: row.rowNumber, companyName: row.companyName, outcome: 'created', cdmoId: created?.id };
        audit = auditRow(row, 'created', { cdmoId: created?.id });
      } catch (error) {
        const reason = describeCreateError(error);
        result = { rowNumber: row.rowNumber, companyName: row.companyName, outcome: 'failed', reason };
        audit = auditRow(row, 'failed', { errors: [...row.errors, reason] });
      }
    }

    results.push(result);
    await writeAudit([audit]);
    onProgress?.(index + 1, total);
  }

  results.sort((a, b) => a.rowNumber - b.rowNumber);
  return { batchId, results, counts: countOutcomes(results), auditFailures };
}
